import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SmtpAccountCreateSchema } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { fieldErrors } from '../lib/form-helpers.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Checkbox, Input } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';

interface FormState {
  display_name: string;
  sender_address: string;
  smtp_host: string;
  smtp_port: string;
  smtp_secure: boolean;
  smtp_username: string;
  smtp_password: string;
  imap_host: string;
  imap_port: string;
  imap_secure: boolean;
}

const EMPTY: FormState = {
  display_name: '',
  sender_address: '',
  smtp_host: '',
  smtp_port: '587',
  smtp_secure: true,
  smtp_username: '',
  smtp_password: '',
  imap_host: '',
  imap_port: '993',
  imap_secure: true,
};

export function NewEmailAccountPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [showImap, setShowImap] = useState(false);

  const create = useMutation({
    mutationFn: (payload: unknown) => api.post('/v1/email-accounts/smtp', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-accounts'] });
      navigate('/email-accounts');
    },
    onError: (err) => setBannerError(extractDetail(err)),
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setBannerError(null);

    const payload: Record<string, unknown> = {
      display_name: form.display_name.trim(),
      sender_address: form.sender_address.trim(),
      smtp_host: form.smtp_host.trim(),
      smtp_port: Number.parseInt(form.smtp_port, 10) || 587,
      smtp_secure: form.smtp_secure,
      smtp_username: form.smtp_username.trim(),
      smtp_password: form.smtp_password,
    };
    if (showImap && form.imap_host.trim()) {
      payload['imap_host'] = form.imap_host.trim();
      payload['imap_port'] = Number.parseInt(form.imap_port, 10) || 993;
      payload['imap_secure'] = form.imap_secure;
    }

    const fieldIssues = fieldErrors(SmtpAccountCreateSchema, payload);
    if (fieldIssues) {
      setErrors(fieldIssues);
      return;
    }
    setErrors(null);
    create.mutate(payload);
  };

  const update = <K extends keyof FormState>(key: K) => (value: FormState[K]): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  return (
    <div className="max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">New email account</h1>
        <p className="text-sm text-ink-muted">
          SMTP credentials below. The password is encrypted at rest with AES-256-GCM
          before storage; the API never returns it.
        </p>
      </header>

      <div className="rounded border border-border bg-surface-alt p-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium">Connect a Gmail / Google Workspace account</p>
          <p className="text-xs text-ink-muted">
            Send and receive via Gmail using OAuth (no app password needed).
          </p>
        </div>
        <a
          href={(((import.meta.env.VITE_API_BASE_URL as string | undefined) ??
            ((import.meta.env.VITE_BASE_PATH as string | undefined) ?? '').replace(/\/$/, '')) +
            '/api/auth/oauth/google/start?intent=connect_email')}
          className="rounded border border-border bg-surface px-3 py-2 text-sm font-medium hover:bg-surface-alt"
        >
          Connect with Google
        </a>
      </div>
      <div className="rounded border border-border bg-surface-alt p-3 text-sm text-ink-muted">
        Microsoft 365 OAuth is scaffolded but not yet wired. Use SMTP below for
        Exchange/Office 365 (with an app password) or any other SMTP server.
      </div>

      <Card>
        <CardHeader>
          <CardTitle>SMTP account</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {bannerError && <FormBanner message={bannerError} />}

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Display name" required error={errors?.['display_name']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.display_name}
                    onChange={(e) => update('display_name')(e.target.value)}
                    placeholder="Sales SMTP"
                    required
                    autoFocus
                  />
                )}
              </FormField>
              <FormField label="Sender address" required error={errors?.['sender_address']}>
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    value={form.sender_address}
                    onChange={(e) => update('sender_address')(e.target.value)}
                    placeholder="noreply@example.com"
                    required
                  />
                )}
              </FormField>
            </div>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">SMTP</legend>
              <div className="grid grid-cols-[1fr_120px] gap-4">
                <FormField label="Host" required error={errors?.['smtp_host']}>
                  {(id) => (
                    <Input
                      id={id}
                      value={form.smtp_host}
                      onChange={(e) => update('smtp_host')(e.target.value)}
                      placeholder="smtp.example.com"
                      required
                    />
                  )}
                </FormField>
                <FormField label="Port" required error={errors?.['smtp_port']}>
                  {(id) => (
                    <Input
                      id={id}
                      type="number"
                      min={1}
                      max={65535}
                      value={form.smtp_port}
                      onChange={(e) => update('smtp_port')(e.target.value)}
                      required
                    />
                  )}
                </FormField>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.smtp_secure}
                  onChange={(e) => update('smtp_secure')(e.target.checked)}
                />
                Use TLS (recommended; uncheck only for plaintext-test scenarios)
              </label>
              <FormField label="Username" required error={errors?.['smtp_username']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.smtp_username}
                    onChange={(e) => update('smtp_username')(e.target.value)}
                    autoComplete="off"
                    required
                  />
                )}
              </FormField>
              <FormField label="Password" required error={errors?.['smtp_password']}>
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    value={form.smtp_password}
                    onChange={(e) => update('smtp_password')(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                )}
              </FormField>
            </fieldset>

            <div>
              <button
                type="button"
                className="text-sm text-accent hover:underline"
                onClick={() => setShowImap((v) => !v)}
              >
                {showImap ? '− Hide IMAP (optional)' : '+ Add IMAP (optional, for future inbound)'}
              </button>
            </div>

            {showImap && (
              <fieldset className="rounded border border-border p-4 space-y-3">
                <legend className="px-1 text-sm font-medium text-ink-muted">
                  IMAP — not yet used; reserved for inbound features
                </legend>
                <div className="grid grid-cols-[1fr_120px] gap-4">
                  <FormField label="IMAP host" error={errors?.['imap_host']}>
                    {(id) => (
                      <Input
                        id={id}
                        value={form.imap_host}
                        onChange={(e) => update('imap_host')(e.target.value)}
                        placeholder="imap.example.com"
                      />
                    )}
                  </FormField>
                  <FormField label="Port" error={errors?.['imap_port']}>
                    {(id) => (
                      <Input
                        id={id}
                        type="number"
                        min={1}
                        max={65535}
                        value={form.imap_port}
                        onChange={(e) => update('imap_port')(e.target.value)}
                      />
                    )}
                  </FormField>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.imap_secure}
                    onChange={(e) => update('imap_secure')(e.target.checked)}
                  />
                  Use TLS
                </label>
              </fieldset>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/email-accounts')}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create account'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
